import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { loadHighlightTagIds } from '@/hooks/useDocumentHighlightTags';
import { firstSchemaCompatible } from '@/lib/markingCitation';
import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database.generated';
import {
  highlightGeometryJson,
  isHighlightColor,
  isHighlightVisibility,
  parseHighlightCellAnchor,
  parseHighlightRects,
  parseHighlightTextAnchor,
  type FileHighlight,
  type HighlightCellAnchor,
  type HighlightColor,
  type HighlightTextAnchor,
  type HighlightVisibility,
} from '@/utils/fileHighlights';

export const documentHighlightsKey = (documentId: string | null | undefined) =>
  ['document-highlights', documentId ?? null] as const;

export interface PreviewDocumentMeta {
  id: string;
  clinic_id: string;
  patient_id: string | null;
  storage_path: string;
  mime_type: string;
  filename: string;
  original_filename: string;
}

type HighlightRow = {
  id: string;
  clinic_id: string;
  document_id: string;
  storage_path: string;
  page: number;
  quote: string;
  prefix: string;
  suffix: string;
  rects: unknown;
  color: string;
  comment: string | null;
  visibility: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  author: { name: string | null } | null;
};

export function mapDocumentHighlight(row: HighlightRow): FileHighlight | null {
  if (!isHighlightColor(row.color) || !isHighlightVisibility(row.visibility)) return null;
  return {
    id: row.id,
    clinic_id: row.clinic_id,
    document_id: row.document_id,
    storage_path: row.storage_path,
    page: row.page,
    quote: row.quote,
    prefix: row.prefix,
    suffix: row.suffix,
    rects: parseHighlightRects(row.rects),
    textAnchor: parseHighlightTextAnchor(row.rects),
    cellAnchor: parseHighlightCellAnchor(row.rects),
    color: row.color,
    comment: row.comment,
    visibility: row.visibility,
    created_by: row.created_by,
    created_at: row.created_at,
    updated_at: row.updated_at,
    author_name: row.author?.name ?? null,
  };
}

async function attachHighlightTagIds<T extends { id: string }>(
  rows: T[],
): Promise<Array<T & { tagIds: string[] }>> {
  const links = await loadHighlightTagIds(rows.map((row) => row.id));
  return rows.map((row) => ({ ...row, tagIds: links.get(row.id) ?? [] }));
}

export function usePreviewDocument(documentId: string | null | undefined) {
  return useQuery({
    queryKey: ['documents', 'preview-meta', documentId ?? null],
    enabled: Boolean(documentId),
    queryFn: async (): Promise<PreviewDocumentMeta> => {
      if (!documentId) throw new Error('missing_document');
      const { data, error } = await supabase
        .from('documents')
        .select('id, clinic_id, patient_id, storage_path, mime_type, filename, original_filename')
        .eq('id', documentId)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export function useDocumentHighlights(documentId: string | null | undefined) {
  return useQuery({
    queryKey: documentHighlightsKey(documentId),
    enabled: Boolean(documentId),
    queryFn: async (): Promise<FileHighlight[]> => {
      if (!documentId) return [];
      const { data, error } = await supabase
        .from('document_highlights')
        .select(
          `id, clinic_id, document_id, storage_path, page, quote, prefix, suffix, rects, color,
           comment, visibility, created_by, created_at, updated_at,
           author:users!document_highlights_created_by_fkey(name)`,
        )
        .eq('document_id', documentId)
        .order('page', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      const rows = ((data ?? []) as unknown as HighlightRow[])
        .map(mapDocumentHighlight)
        .filter((row): row is FileHighlight => row !== null);
      return attachHighlightTagIds(rows);
    },
  });
}

export function useCreateDocumentHighlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      clinicId: string;
      documentId: string;
      storagePath: string;
      createdBy: string;
      page: number;
      quote: string;
      prefix: string;
      suffix: string;
      rects: FileHighlight['rects'];
      textAnchor: HighlightTextAnchor | null;
      cellAnchor: HighlightCellAnchor | null;
      color: HighlightColor;
      comment: string | null;
      visibility: HighlightVisibility;
    }): Promise<FileHighlight> => {
      const { data, error } = await supabase
        .from('document_highlights')
        .insert({
          clinic_id: input.clinicId,
          document_id: input.documentId,
          storage_path: input.storagePath,
          created_by: input.createdBy,
          page: input.page,
          quote: input.quote,
          prefix: input.prefix,
          suffix: input.suffix,
          rects: highlightGeometryJson(
            input.rects,
            input.textAnchor,
            input.cellAnchor,
          ) as unknown as Json,
          color: input.color,
          comment: input.comment,
          visibility: input.visibility,
        })
        .select(
          `id, clinic_id, document_id, storage_path, page, quote, prefix, suffix, rects, color,
           comment, visibility, created_by, created_at, updated_at,
           author:users!document_highlights_created_by_fkey(name)`,
        )
        .single();
      if (error) throw error;
      const mapped = mapDocumentHighlight(data as unknown as HighlightRow);
      if (!mapped) throw new Error('highlight_invalid');
      return mapped;
    },
    onSuccess: (row) => {
      refreshHighlightQueries(qc, row.document_id);
    },
  });
}

export function useUpdateDocumentHighlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      documentId: string;
      color?: HighlightColor;
      comment?: string | null;
      visibility?: HighlightVisibility;
    }): Promise<void> => {
      const { error } = await supabase
        .from('document_highlights')
        .update({
          ...(input.color ? { color: input.color } : {}),
          ...(input.comment !== undefined ? { comment: input.comment } : {}),
          ...(input.visibility ? { visibility: input.visibility } : {}),
        })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_void, input) => {
      refreshHighlightQueries(qc, input.documentId);
    },
  });
}

export function useDeleteDocumentHighlight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; documentId: string }): Promise<void> => {
      const { error } = await supabase.from('document_highlights').delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_void, input) => {
      refreshHighlightQueries(qc, input.documentId);
    },
  });
}

export const patientFileHighlightsRoot = ['patient-file-highlights'] as const;

function refreshHighlightQueries(qc: ReturnType<typeof useQueryClient>, documentId: string) {
  void qc.invalidateQueries({ queryKey: documentHighlightsKey(documentId) });
  void qc.invalidateQueries({ queryKey: patientFileHighlightsRoot });
}

export type PatientFileHighlight = FileHighlight & {
  filename: string;
  mime_type: string;
  /** Dokumentnavn. Empty until Dokument info is filled in. */
  document_title: string | null;
  /** Af/forfatter on the file. Not the staff user who made the marking. */
  document_author: string | null;
  /** Letter or scan date from the file. Null only when the column could not be read. */
  document_date: string | null;
};

export const patientFileHighlightsKey = (
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) => ['patient-file-highlights', clinicId ?? null, patientId ?? null] as const;

type PatientDocumentMeta = {
  id: string;
  original_filename: string;
  mime_type: string;
  document_date: string | null;
  document_title: string | null;
  document_author: string | null;
};

function toPatientDocumentMeta(row: {
  id: string;
  original_filename: string;
  mime_type: string;
  document_date?: string | null;
  document_title?: string | null;
  document_author?: string | null;
}): PatientDocumentMeta {
  return {
    id: row.id,
    original_filename: row.original_filename,
    mime_type: row.mime_type,
    document_date: row.document_date ?? null,
    document_title: row.document_title ?? null,
    document_author: row.document_author ?? null,
  };
}

/**
 * Markings on this patient's files. Reads `document_highlights` for
 * non-archived documents owned by the patient. RLS still hides another
 * person's private markings. No extra table.
 */
export function usePatientFileHighlights(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: patientFileHighlightsKey(clinicId, patientId),
    enabled: Boolean(clinicId) && Boolean(patientId),
    staleTime: 15_000,
    queryFn: async (): Promise<PatientFileHighlight[]> => {
      if (!clinicId || !patientId) return [];
      // Title and author arrived with Dokument info. Until that migration has
      // run, the narrower reads still return the file name and document date.
      const documents = await firstSchemaCompatible<PatientDocumentMeta>([
        async () => {
          const { data, error } = await supabase
            .from('documents')
            .select(
              'id, original_filename, mime_type, document_date, document_title, document_author',
            )
            .eq('clinic_id', clinicId)
            .eq('patient_id', patientId)
            .is('archived_at', null);
          return { data: data?.map(toPatientDocumentMeta) ?? null, error };
        },
        async () => {
          const { data, error } = await supabase
            .from('documents')
            .select('id, original_filename, mime_type, document_date')
            .eq('clinic_id', clinicId)
            .eq('patient_id', patientId)
            .is('archived_at', null);
          return { data: data?.map(toPatientDocumentMeta) ?? null, error };
        },
        async () => {
          const { data, error } = await supabase
            .from('documents')
            .select('id, original_filename, mime_type')
            .eq('clinic_id', clinicId)
            .eq('patient_id', patientId)
            .is('archived_at', null);
          return { data: data?.map(toPatientDocumentMeta) ?? null, error };
        },
      ]);
      if (documents.length === 0) return [];

      const byId = new Map(documents.map((doc) => [doc.id, doc]));
      const ids = [...byId.keys()];
      const highlights: PatientFileHighlight[] = [];
      const chunkSize = 80;
      for (let index = 0; index < ids.length; index += chunkSize) {
        const slice = ids.slice(index, index + chunkSize);
        const { data, error } = await supabase
          .from('document_highlights')
          .select(
            `id, clinic_id, document_id, storage_path, page, quote, prefix, suffix, rects, color,
             comment, visibility, created_by, created_at, updated_at,
             author:users!document_highlights_created_by_fkey(name)`,
          )
          .eq('clinic_id', clinicId)
          .in('document_id', slice);
        if (error) throw error;
        const chunk: PatientFileHighlight[] = [];
        for (const raw of (data ?? []) as unknown as HighlightRow[]) {
          const mapped = mapDocumentHighlight(raw);
          const doc = mapped ? byId.get(mapped.document_id) : undefined;
          if (!mapped || !doc) continue;
          chunk.push({
            ...mapped,
            filename: doc.original_filename,
            mime_type: doc.mime_type,
            document_title: doc.document_title,
            document_author: doc.document_author,
            document_date: doc.document_date,
          });
        }
        highlights.push(...(await attachHighlightTagIds(chunk)));
      }
      highlights.sort(
        (a, b) => b.created_at.localeCompare(a.created_at) || a.id.localeCompare(b.id),
      );
      return highlights;
    },
  });
}
